import React from 'react';
import { ShieldAlert, ShieldCheck, MapPin, Zap, AlertTriangle, BrainCircuit, Target } from 'lucide-react';

const BudgetAiAnalysisDisplay = ({ report }) => {
  if (!report || !report.coaching_briefing) return null;

  const { qualitative_summary, triage_briefing, coaching_briefing, framework_calibration, actionable_advice } = report;

  return (
    <div className="mt-6 p-6 bg-slate-900 rounded-2xl border border-slate-700 text-left animate-fade-in shadow-xl text-white">
      
      {/* HEADER & QUALITATIVE SUMMARY */}
      <div className="mb-6 border-b border-slate-700 pb-6">
        <h3 className="text-2xl font-black text-white mb-3 uppercase tracking-widest flex items-center gap-2">
            <Zap className="text-emerald-400" /> Senior Coach Briefing
        </h3>
        <p className="text-lg text-slate-300 italic font-serif leading-relaxed border-l-4 border-indigo-500 pl-4">
            "{qualitative_summary}"
        </p>
      </div>

      {/* MATHEMATICAL DEADLOCK ALERT (Shifted to Environmental Critique) */}
      {triage_briefing && (
        <div className="mb-6 p-5 bg-red-900/40 border-l-4 border-red-500 rounded-r-xl flex items-start gap-4 shadow-inner">
           <AlertTriangle className="text-red-500 flex-shrink-0 mt-1" />
           <div>
              <h4 className="font-black text-red-400 uppercase tracking-widest text-xs mb-2">Environmental Trap Detected</h4>
              <p className="text-red-100 text-sm leading-relaxed font-medium">{triage_briefing}</p>
              <p className="text-red-300 text-xs mt-3 italic border-t border-red-500/30 pt-2 font-bold">
                  System Note: This is a systemic cost-of-living failure, not a personal budgeting failure. The market is mathematically hostile.
              </p>
           </div>
        </div>
      )}

      {/* FRAMEWORK CALIBRATION */}
      {framework_calibration && (
         <div className="mb-8 p-5 bg-indigo-900/40 border border-indigo-500/50 rounded-xl">
            <h4 className="font-bold text-indigo-200 uppercase tracking-widest text-xs mb-2 flex items-center gap-2">
                <Target size={14} /> Framework Check: {framework_calibration.mode}
            </h4>
            <p className="text-indigo-100 text-sm leading-relaxed">{framework_calibration.analysis}</p>
         </div>
      )}

      {/* COACHING VERTICALS */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <div className="bg-slate-800 p-5 rounded-xl border border-slate-600">
              <h4 className="font-bold text-orange-400 mb-2 flex items-center gap-2 text-sm uppercase tracking-wider">
                  <ShieldAlert size={16} /> Tax Bomb Alert
              </h4>
              <p className="text-slate-300 text-sm leading-relaxed">{coaching_briefing.tax_bomb_alert}</p>
          </div>

          <div className="bg-slate-800 p-5 rounded-xl border border-slate-600">
              <h4 className="font-bold text-blue-400 mb-2 flex items-center gap-2 text-sm uppercase tracking-wider">
                  <ShieldCheck size={16} /> Trust Score Strategy
              </h4>
              <p className="text-slate-300 text-sm leading-relaxed">{coaching_briefing.trust_score_strategy}</p>
          </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <div className="bg-slate-800 p-5 rounded-xl border border-slate-600">
              <h4 className="font-bold text-emerald-400 mb-2 flex items-center gap-2 text-sm uppercase tracking-wider">
                  <Zap size={16} /> Lifestyle Pivot
              </h4>
              <p className="text-slate-300 text-sm leading-relaxed">{coaching_briefing.lifestyle_pivot}</p>
          </div>

          <div className="bg-slate-800 p-5 rounded-xl border border-slate-600">
              <h4 className="font-bold text-purple-400 mb-2 flex items-center gap-2 text-sm uppercase tracking-wider">
                  <MapPin size={16} /> Regional Context
              </h4>
              <p className="text-slate-300 text-sm leading-relaxed">{coaching_briefing.regional_context}</p>
          </div>
      </div>

      {/* ACTIONABLE ADVICE LIST */}
      {actionable_advice && actionable_advice.length > 0 && (
          <div className="bg-slate-800/50 p-5 rounded-xl border border-slate-700">
              <h4 className="font-bold text-emerald-400 mb-3 flex items-center gap-2 text-sm uppercase tracking-wider">
                  <BrainCircuit size={16} /> Tactical Execution
              </h4>
              <ul className="space-y-3">
                  {actionable_advice.map((tip, index) => (
                      <li key={index} className="flex items-start gap-3 text-sm text-slate-300 leading-relaxed">
                          <div className="w-5 h-5 rounded bg-slate-700 text-slate-400 flex items-center justify-center flex-shrink-0 text-xs font-bold mt-0.5">
                              {index + 1}
                          </div>
                          {tip}
                      </li>
                  ))}
              </ul>
          </div>
      )}
    </div>
  );
};

export default BudgetAiAnalysisDisplay;